// Interop layer for Blazor to interact with photo storage
// Uses object URLs instead of base64 for efficient image display

import { photoStorage } from './photo-storage';
import { photoHelpers } from './photo-helpers';

interface CapturePhotoResult {
    blobName: string;
    thumbnailUrl: string;
    fileSizeBytes: number;
    contentType: string;
}

// Track object URLs so we can revoke them when no longer needed
const activeObjectUrls = new Map<string, string>();

export const photoStorageInterop = {
    async captureAndCachePhoto(
        inputElement: HTMLInputElement,
        surveyId: string,
        fieldId: string
    ): Promise<CapturePhotoResult | null> {
        const photoData = await photoHelpers.readPhotoFile(inputElement);
        if (!photoData) {
            return null;
        }

        const extension = getExtensionFromContentType(photoData.contentType);
        const blobName = `${crypto.randomUUID()}${extension}`;

        await photoStorage.cachePhoto(
            blobName,
            surveyId,
            fieldId,
            photoData.contentType,
            photoData.imageBlob,
            photoData.thumbnailBlob
        );

        // Create object URL for thumbnail display
        const thumbnailUrl = URL.createObjectURL(photoData.thumbnailBlob);
        activeObjectUrls.set(`thumb_${blobName}`, thumbnailUrl);

        return {
            blobName,
            thumbnailUrl,
            fileSizeBytes: photoData.fileSizeBytes,
            contentType: photoData.contentType
        };
    },

    async getThumbnailUrl(blobName: string): Promise<string | null> {
        // Check if we already have an active URL
        const existingUrl = activeObjectUrls.get(`thumb_${blobName}`);
        if (existingUrl) {
            return existingUrl;
        }

        const thumbnailBlob = await photoStorage.getThumbnailBlob(blobName);
        if (!thumbnailBlob) {
            return null;
        }

        const url = URL.createObjectURL(thumbnailBlob);
        activeObjectUrls.set(`thumb_${blobName}`, url);
        return url;
    },

    async getPhotoUrl(blobName: string): Promise<string | null> {
        // Check if we already have an active URL
        const existingUrl = activeObjectUrls.get(`photo_${blobName}`);
        if (existingUrl) {
            return existingUrl;
        }

        const imageBlob = await photoStorage.getImageBlob(blobName);
        if (!imageBlob) {
            return null;
        }

        const url = URL.createObjectURL(imageBlob);
        activeObjectUrls.set(`photo_${blobName}`, url);
        return url;
    },

    revokePhotoUrl(blobName: string): void {
        const photoUrl = activeObjectUrls.get(`photo_${blobName}`);
        if (photoUrl) {
            URL.revokeObjectURL(photoUrl);
            activeObjectUrls.delete(`photo_${blobName}`);
        }
    },

    revokeThumbnailUrl(blobName: string): void {
        const thumbUrl = activeObjectUrls.get(`thumb_${blobName}`);
        if (thumbUrl) {
            URL.revokeObjectURL(thumbUrl);
            activeObjectUrls.delete(`thumb_${blobName}`);
        }
    },

    revokeAllUrls(blobName: string): void {
        this.revokePhotoUrl(blobName);
        this.revokeThumbnailUrl(blobName);
    }
};

function getExtensionFromContentType(contentType: string): string {
    switch (contentType.toLowerCase()) {
        case 'image/jpeg': return '.jpg';
        case 'image/png': return '.png';
        case 'image/gif': return '.gif';
        case 'image/webp': return '.webp';
        case 'image/heic': return '.heic';
        case 'image/heif': return '.heif';
        default: return '.jpg';
    }
}
